import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  Wrench, Search, CheckCircle2,
  ArrowUp, ArrowDown, ChevronsUpDown,
  ChevronLeft, ChevronRight, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import type { AxiosError } from 'axios';
import type { SellerServiceListItem } from '@m8-app/contracts';
import { getAllSellerServices, resolveServiceRequest } from '../api/seller.js';
import { TableSkeleton } from '../components/ui/index.js';
import { cn } from '../components/ui/cn.js';

const PAGE_SIZE = 10;
type SortCol     = 'serialNumber' | 'createdAt';
type SortDir     = 'asc' | 'desc';
type StatusFilter = 'all' | 'open' | 'resolved';

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all',      label: 'All'      },
  { value: 'open',     label: 'Open'     },
  { value: 'resolved', label: 'Resolved' },
];

function Th({ col, label, sort, onSort }: {
  col: SortCol; label: string;
  sort: { col: SortCol; dir: SortDir }; onSort: (c: SortCol) => void;
}) {
  const active = sort.col === col;
  return (
    <th onClick={() => onSort(col)}
      className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide cursor-pointer select-none hover:text-slate-700 transition-colors">
      <div className="flex items-center gap-1">
        {label}
        {active
          ? sort.dir === 'asc' ? <ArrowUp className="w-3 h-3 text-blue-500" /> : <ArrowDown className="w-3 h-3 text-blue-500" />
          : <ChevronsUpDown className="w-3 h-3 text-slate-300" />}
      </div>
    </th>
  );
}

function StatusPill({ status }: { status: 'open' | 'resolved' }) {
  return (
    <span className={cn(
      'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-xs font-semibold ring-1',
      status === 'open' ? 'bg-amber-50 text-amber-700 ring-amber-200/60' : 'bg-emerald-50 text-emerald-700 ring-emerald-200/60',
    )}>
      <span className={cn('w-1.5 h-1.5 rounded-full', status === 'open' ? 'bg-amber-400 animate-pulse' : 'bg-emerald-400')} />
      {status === 'open' ? 'Open' : 'Resolved'}
    </span>
  );
}

export function ServicesPage() {
  const navigate = useNavigate();

  const [search, setSearch]           = useState('');
  const [statusFilter, setStatus]     = useState<StatusFilter>('all');
  const [sort, setSort]               = useState<{ col: SortCol; dir: SortDir }>({ col: 'createdAt', dir: 'desc' });
  const [page, setPage]               = useState(1);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const qc = useQueryClient();

  const { data, isLoading, isError, error } = useQuery({ queryKey: ['seller-services'], queryFn: getAllSellerServices });

  const resolveMutation = useMutation({
    mutationFn: (requestId: string) => resolveServiceRequest(requestId),
    onSuccess: () => {
      toast.success('Marked as resolved');
      qc.invalidateQueries({ queryKey: ['seller-services'] });
      setResolvingId(null);
    },
    onError: () => {
      toast.error('Failed to resolve request');
      setResolvingId(null);
    },
  });

  useEffect(() => {
    if (isError) toast.error((error as AxiosError<{ message?: string }>)?.response?.data?.message ?? 'Failed to load service requests.');
  }, [isError, error]);

  useEffect(() => { setPage(1); }, [search, statusFilter]);

  function toggleSort(col: SortCol) {
    setSort((prev) => prev.col === col ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { col, dir: 'asc' });
  }

  const requests: SellerServiceListItem[] = data?.requests ?? [];

  const filtered = requests.filter((r) => {
    const q = search.toLowerCase();
    return (q === '' || r.serialNumber.toLowerCase().includes(q) || r.problem.toLowerCase().includes(q))
      && (statusFilter === 'all' || r.status === statusFilter);
  });

  const sorted = [...filtered].sort((a, b) => {
    const cmp = sort.col === 'serialNumber' ? a.serialNumber.localeCompare(b.serialNumber)
      : new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return sort.dir === 'asc' ? cmp : -cmp;
  });

  const totalPages  = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const paged       = sorted.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const openCount   = requests.filter((r) => r.status === 'open').length;
  const resolvedCount = requests.filter((r) => r.status === 'resolved').length;

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0 mt-0.5">
          <Wrench className="w-4 h-4 text-blue-600" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900">Services Management</h1>
          <p className="text-sm text-slate-500 mt-0.5">All service requests logged against your registered devices.</p>
        </div>
      </div>

      {/* Stat chips */}
      {!isLoading && requests.length > 0 && (
        <div className="flex gap-2.5 mb-5">
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-100 rounded-lg px-3.5 py-2">
            <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-xs font-semibold text-amber-700">{openCount} Open</span>
          </div>
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-lg px-3.5 py-2">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            <span className="text-xs font-semibold text-emerald-700">{resolvedCount} Resolved</span>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-col sm:flex-row gap-2.5 mb-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search serial or problem…"
            className="w-full pl-8 pr-3 py-2 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-0.5">
          {STATUS_OPTIONS.map((s) => (
            <button key={s.value} type="button" onClick={() => setStatus(s.value)}
              className={cn('px-3 py-1.5 rounded-md text-xs font-semibold transition-all',
                statusFilter === s.value ? 'bg-white shadow-sm text-slate-800' : 'text-slate-500 hover:text-slate-700')}>
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && <TableSkeleton rows={5} cols={4} />}

      {!isLoading && sorted.length === 0 && (
        <div className="bg-slate-50 border border-dashed border-slate-200 rounded-xl px-6 py-14 text-center">
          <AlertCircle className="w-6 h-6 text-slate-300 mx-auto mb-2.5" />
          <p className="text-sm font-medium text-slate-500">
            {requests.length === 0 ? 'No service requests yet.' : 'No results found.'}
          </p>
          <p className="text-xs text-slate-400 mt-1">
            {requests.length === 0
              ? 'Log a request from the Device List or Device Details page.'
              : 'Try adjusting your search or filter.'}
          </p>
        </div>
      )}

      {!isLoading && paged.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/80 border-b border-slate-100">
              <tr>
                <Th col="serialNumber" label="Serial number" sort={sort} onSort={toggleSort} />
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Problem</th>
                <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Status</th>
                <Th col="createdAt" label="Logged on" sort={sort} onSort={toggleSort} />
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody>
              {paged.map((item, i) => (
                <motion.tr
                  key={item.id}
                  initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                  transition={{ duration: 0.12, delay: i * 0.02 }}
                  onClick={() => navigate(`/devices/${item.registrationId}`)}
                  className={cn('cursor-pointer hover:bg-blue-50/30 transition-colors', i < paged.length - 1 && 'border-b border-slate-50')}
                >
                  <td className="px-5 py-3.5 font-mono text-slate-700 text-xs">{item.serialNumber}</td>
                  <td className="px-5 py-3.5 text-slate-600 max-w-70">
                    <p className="truncate">{item.problem}</p>
                  </td>
                  <td className="px-5 py-3.5"><StatusPill status={item.status} /></td>
                  <td className="px-5 py-3.5 text-slate-400 text-xs tabular-nums">
                    {new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(item.createdAt))}
                  </td>
                  <td className="px-3 py-3.5 text-right">
                    {item.status === 'open' && (
                      <button type="button" title="Mark as resolved"
                        onClick={(e) => { e.stopPropagation(); setResolvingId(item.id); resolveMutation.mutate(item.id); }}
                        disabled={resolvingId === item.id}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 transition-colors">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Resolve
                      </button>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100">
            <span className="text-xs text-slate-400">
              {sorted.length === 0 ? '0 results'
                : `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, sorted.length)} of ${sorted.length}`}
            </span>
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setPage((p) => p - 1)} disabled={page === 1}
                className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors" aria-label="Previous">
                <ChevronLeft className="w-4 h-4 text-slate-500" />
              </button>
              <span className="px-2.5 text-xs font-semibold text-slate-600 tabular-nums">{page}/{totalPages}</span>
              <button type="button" onClick={() => setPage((p) => p + 1)} disabled={page >= totalPages}
                className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 transition-colors" aria-label="Next">
                <ChevronRight className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
